/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/prop-types */
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSortBy, useTable } from "react-table";
import { GetOrders200Response } from "../../codegen-api";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { getAssetFromSymbol } from "../../utils/instruments";
import { getStopOrderName } from "../../utils/order";
import { CancelAllOrdersModal } from "../ConfirmationModal/CancelAllOrdersModal";
import EditOrderModal from "../ConfirmationModal/EditOrderModal";
import RemoveOrderModal from "../ConfirmationModal/RemoveOrderModal";
import { Spinner } from "../shared/Spinner";
import TableLoadMore from "../shared/Table/TableLoadMore";
import { SpinnerContainerWrapper } from "../shared/Spinner/style";
import { DefaultCellForColumn } from "../shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../shared/Table/DefaultHeaderForColumn";
import { TableHeaderCell } from "../shared/Table/style";
import OrderDetailsModal from "../shared/ViewDetailsModal/OrderDetailsModal";
import { OrdersTableWrapper } from "./style";
import { MarketContext } from "../../contexts/MarketContext";
import { isInstrumentTypeEnabled } from "../../config/features";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useInstrumentResolver } from "../../hooks/markets/useInstrumentResolver";
import { getOrdersColumns } from "./columns";
import { IModifiedOrder, IOrdersProps } from "./types";

function Orders({
  orders,
  loading,
  selectedMarketType,
  selectedAssetType,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: IOrdersProps) {
  const { t } = useTranslation("app", { keyPrefix: "Orders" });
  const { t: translateAll } = useTranslation();
  const { t: tooltip } = useTranslation("tooltips");
  const {
    setOrderType,
    setExpiry,
    setStrike,
  } = useContext(MarketInstrumentContext);
  const { data: accountData } = useGetAccount();
  const {
    resolveInstrumentName,
    resolveInstrumentAsset,
    resolveMarketPrecision,
  } = useInstrumentResolver();

  const [removingOrder, setRemovingOrder] = useState<GetOrders200Response>();
  const [viewDetailsForOrder, setViewDetailsForOrder] =
    useState<GetOrders200Response>();
  const [showRemoveOrderModal, setShowRemoveOrderModal] = useState(false);

  const [editingOrder, setEditingOrder] = useState<GetOrders200Response>();
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [showCancelAllOrderModal, setShowCancelAllOrderModal] = useState(false);

  const onClickCancelAll = useCallback(() => {
    setShowCancelAllOrderModal(true);
  }, []);

  const onCancelOrder = useCallback((order?: GetOrders200Response) => {
    setRemovingOrder(order);
    setShowRemoveOrderModal(!!order);
  }, []);

  const onEditOrder = useCallback((order?: GetOrders200Response) => {
    setEditingOrder(order);
    setShowEditOrderModal(!!order);
  }, []);

  const { setMarket } = useContext(MarketContext);

  const onSelectOrder = (order: GetOrders200Response) => {
    if (order) {
      // Rows for a switched-off vertical stay listed (so a user is never locked
      // out of an existing order), but they are not actionable — opening one
      // would bounce the user to the default market. Leave them where they are.
      if (!isInstrumentTypeEnabled(order.instrument_type)) {
        return;
      }
      setMarket({
        derivative: order.instrument_type,
        asset: (resolveInstrumentAsset(order) ||
          getAssetFromSymbol(order.instrument_name)) as string,
      });
      setOrderType(order.option_type);
      setExpiry(Number(order.expiry));
      setStrike(order.strike);
    }
  };

  const getInstrumentLeverage = useCallback(
    (instrumentId: string) =>
      accountData?.leverages?.find((l) => l.instrument_id === instrumentId),
    [accountData?.leverages]
  );

  const modifiedOrders: IModifiedOrder[] = useMemo(
    () => {
      return orders.map((order) => {
        const resolvedInstrumentName =
          resolveInstrumentName(order) || order.instrument_name;
        const marketPrecision = resolveMarketPrecision(order);

        // API returns human-readable values — no wei conversion needed
        const amountNum = Number(order.amount || 0);
        const filledNum = Number(order.filled || 0);

        const modifiedOrder = {
          ...order,
          instrument_name: resolvedInstrumentName,
          fillPercent: amountNum > 0 ? (
            (filledNum / amountNum) *
            100
          ).toFixed(1) : "0",
          remaining: (amountNum - filledNum).toFixed(2),
          tradeDirection: order.side,
          amountPrecision: marketPrecision.amount_precision,
          type: order.stop
            ? `${getStopOrderName(
                translateAll,
                order.order_type,
                order.stop,
                true
              )}`
            : order.order_type,
        };

        return modifiedOrder;
      });
    },
    [orders, resolveInstrumentName, resolveMarketPrecision, translateAll]
  );

  const columns: ITableColumn<IModifiedOrder>[] = useMemo(
    () =>
      getOrdersColumns(
        t,
        tooltip,
        selectedMarketType,
        resolveInstrumentName,
        resolveMarketPrecision,
        getInstrumentLeverage,
        translateAll,
        onClickCancelAll,
        onCancelOrder,
        onEditOrder
      ),
    [
      t,
      tooltip,
      selectedMarketType,
      resolveInstrumentName,
      resolveMarketPrecision,
      getInstrumentLeverage,
      translateAll,
      onClickCancelAll,
      onCancelOrder,
      onEditOrder,
    ]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: modifiedOrders,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        },
        autoResetSortBy: false,
      } as any,
      useSortBy
    );

  if (loading) {
    return (
      <SpinnerContainerWrapper>
        <Spinner />
      </SpinnerContainerWrapper>
    );
  }

  return (
    <>
      <RemoveOrderModal
        order={removingOrder}
        onHide={() => setShowRemoveOrderModal(false)}
        show={showRemoveOrderModal}
      />
      <CancelAllOrdersModal
        instrumentType={selectedMarketType}
        asset={selectedAssetType}
        orders={modifiedOrders}
        show={showCancelAllOrderModal}
        onHide={() => setShowCancelAllOrderModal(false)}
      />
      <EditOrderModal
        order={editingOrder}
        onHide={() => setShowEditOrderModal(false)}
        show={showEditOrderModal}
      />
      <OrderDetailsModal
        order={viewDetailsForOrder}
        onHide={() => setViewDetailsForOrder(undefined)}
        onEditOrder={() => {
          onEditOrder(viewDetailsForOrder);
          setViewDetailsForOrder(undefined);
        }}
        onRemoveOrder={() => {
          onCancelOrder(viewDetailsForOrder);
          setViewDetailsForOrder(undefined);
        }}
      />
      <OrdersTableWrapper>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...trProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...trProps}>
                  {headerGroup.headers.map((column: any) => {
                    const { key, ...tableHeaderCellProps } = column.getHeaderProps(column.getSortByToggleProps());
                    return (
                      <TableHeaderCell key={key} {...tableHeaderCellProps}
                        // style={{ width: column.width }}
                      >
                        {column.render("Header")}
                      </TableHeaderCell>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const { key, ...rowProps } = row.getRowProps();
              return (
                <tr
                  key={key}
                  {...rowProps}
                  onClick={() =>
                    onSelectOrder(row.original as GetOrders200Response)
                  }
                >
                  {row.cells.map((cell) => {
                    const { key, ...tdProps } = cell.getCellProps();
                    return (
                      <td key={key} {...tdProps}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
            {onLoadMore && (
              <TableLoadMore
                colSpan={columns.length}
                hasMore={!!hasMore}
                isLoadingMore={!!isLoadingMore}
                onLoadMore={onLoadMore}
                itemCount={rows.length}
              />
            )}
          </tbody>
        </table>
      </OrdersTableWrapper>
    </>
  );
}

export default memo(Orders);
